import React from 'react';
import { Space, Switch, Tooltip } from 'antd';
import { SunOutlined, MoonOutlined, LogoutOutlined, SettingOutlined, MenuOutlined } from '@ant-design/icons';
import { logout } from '../../api/api';
import { FONT_SIZE, FONT_WEIGHT, ICON_SIZE, SPACING, LAYOUT, SHADOW } from '../../constants/constants';
import { useTheme } from '../../contexts/ThemeContext';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { useStorage } from '../../contexts/StorageContext';
import { useNavigation } from '../../contexts/NavigationContext';
import { useIsCompact, useIsPhone } from '../../hooks/UseIsCompact';
import { useSidebarDrawer } from '../../hooks/UseSidebarDrawer';
import { AppButton } from '../common/AppButton';
import { AppTag } from '../common/AppTag';

interface DashboardHeaderProps {
  title: string;
}

/**
 * 대시보드 상단 헤더 바.
 * 넓은 폭에서는 사이드바(220px) 오른쪽에 position:fixed 로 고정된다.
 *
 * 좁은 폭(<= 999px)에서는 사이드바를 그리지 않고(AppSidebar 가 Drawer 로 접힌다)
 * 헤더가 화면 왼쪽 끝(left:0)에서 시작하며, 헤더 왼쪽의 햄버거 버튼이 Drawer 를 연다.
 * 오른쪽 컨트롤(테마 토글·로그아웃)도 폭을 줄이려고 아이콘만 남긴다.
 *
 * 색상은 전부 디자인 시스템 토큰(useThemeTokens)을 사용한다.
 */
export const DashboardHeader: React.FC<DashboardHeaderProps> = ({ title }) => {
  const { isDark, toggleDark } = useTheme();
  const t = useThemeTokens();
  const { isAdmin, store, superStore, handleLogout } = useStorage();
  const nav = useNavigation();
  // DashboardLayout·AppSidebar 와 같은 훅(같은 matchMedia 질의)을 써서 폭 판단이 어긋나지 않게 한다.
  const compact = useIsCompact();
  // 헤더 아이콘 버튼은 26×26(sm)이라 데스크톱 최소(24px)는 넘겼지만 390px 화면에서
  // 손가락으로 누르기엔 작았다 — 휴대폰 폭은 44px, 좁은 폭(태블릿)은 32px 로 키운다
  // (2026-10-20 코덱 3차 지적). 마우스로 쓰는 넓은 폭은 원래 크기(26px)를 유지한다 —
  // 한때 넓은 폭까지 32px 로 바뀌어 데스크톱 헤더가 함께 커졌다(같은 날 리뷰 후속).
  const phone = useIsPhone();
  const headerBtnSize = phone ? 'lg' : compact ? 'md' : 'sm';
  const { openDrawer, closeDrawer } = useSidebarDrawer();

  const handleClickLogout = async () => {
    await logout();
    // 좁은 폭 Drawer 열림 상태는 모듈 전역이라, 로그아웃 뒤에도 남아 다음 로그인 때 메뉴가
    // 열린 채로 시작할 수 있다 — 로그아웃 시 닫아 둔다(2026-10-20 리뷰).
    closeDrawer();
    handleLogout();
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: compact ? 0 : LAYOUT.sidebarWidth,
        right: 0,
        height: LAYOUT.headerHeight,
        background: t.bgRaised,
        borderBottom: `1px solid ${t.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: compact ? `0 ${SPACING.sm}px` : `0 ${SPACING.xl}px`,
        gap: SPACING.sm,
        zIndex: 200,
        boxShadow: SHADOW.card,
      }}
    >
      {/* 좌측 — (좁은 폭) 햄버거 + 페이지 제목 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: SPACING.sm,
          minWidth: 0,
          flex: 1,
        }}
      >
        {compact && (
          <AppButton
            variant="text"
            size={headerBtnSize}
            iconOnly
            aria-label="메뉴 열기"
            icon={<MenuOutlined />}
            style={{ color: t.textSecondary, flexShrink: 0 }}
            onClick={openDrawer}
          />
        )}
        <div
          style={{
            fontSize: FONT_SIZE.md,
            fontWeight: FONT_WEIGHT.semibold,
            color: t.textPrimary,
            letterSpacing: '0.01em',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {title}
        </div>
      </div>

      {/* 우측 컨트롤 — 세로 중앙 정렬(align)로 높이가 다른 요소들을 한 줄에 맞춘다 */}
      <Space size={compact ? SPACING.xs : SPACING.sm} align="center" style={{ flexShrink: 0 }}>
        <AppTag variant="role" isAdmin={isAdmin} />
        <AppTag variant="store" code={store ?? superStore} />
        {/* 테마 토글: 해·스위치·달을 하나의 묶음으로(작은 간격) 묶어 중앙 정렬.
            좁은 폭에서는 해·달 아이콘을 빼고 스위치만 남겨 폭을 아낀다. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: SPACING.xs }}>
          {!compact && <SunOutlined style={{ color: t.iconSun, fontSize: ICON_SIZE.sm, display: 'block' }} />}
          {/* 크기는 buildAntTheme 의 Switch 토큰(24px 높이)을 따른다 — size="small"(28×16)은
              클릭 대상이 너무 작았다(2026-10-20 감사 "화면 · 크기"). */}
          {/* 휴대폰 폭에서는 스위치(40×24)를 44px 누르는 곳으로 감싼다 — label 을 눌러도 스위치가 눌린다
              (2026-10-21 코덱 화면 점검, 감사 card-controltower#63). */}
          {/* 누르는 곳 44px — 휴대폰만이던 것을 PC 에서도(코덱 화면 점검 2026-10-30 낮음, 감사 #518). */}
          <label style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            minWidth: 44, minHeight: 44, cursor: 'pointer',
          }}>
            <Switch checked={isDark} onChange={toggleDark} aria-label="다크 모드 전환" />
          </label>
          {!compact && <MoonOutlined style={{ color: t.iconMoon, fontSize: ICON_SIZE.sm, display: 'block' }} />}
        </div>
        {isAdmin && (
          <Tooltip title="매장 설정">
            <AppButton
              variant="text"
              size={headerBtnSize}
              iconOnly
              aria-label="매장 설정"
              icon={<SettingOutlined />}
              style={{ color: nav.page === 'settings' ? t.accent : t.textSecondary, minWidth: 44, minHeight: 44 }}
              onClick={() => nav.go('settings')}
            />
          </Tooltip>
        )}
        <AppButton
          variant="text"
          size={headerBtnSize}
          icon={<LogoutOutlined />}
          iconOnly={compact}
          aria-label="로그아웃"
          style={{ color: t.textSecondary, minWidth: 44, minHeight: 44 }}
          onClick={handleClickLogout}
        >
          {!compact && '로그아웃'}
        </AppButton>
      </Space>
    </div>
  );
};
